import type { FilterGroupV1Option } from '@/features/filter/filter-data-v1-type';

import React from 'react';

import chunk from 'lodash.chunk';

import { IconImage } from '@/core/features';
import { getLastElement } from '@/core/utils/array';
import { bemModule } from '@/core/utils/bem-classname';
import useFilterChip from '@/features/filter/filter-chip/use-filter-chip';

import styles from './filter-button.module.scss';

const bem = bemModule(styles);

export default function FilterButton({
    columns,
    filterOption,
    index,
    isLoading,
    onFilterChipClick,
    optionCount,
    showDefaultSelected,
}: {
    columns: number;
    filterOption: FilterGroupV1Option;
    index: number;
    isLoading: boolean;
    onFilterChipClick: (option: FilterGroupV1Option) => void;
    optionCount: number;
    showDefaultSelected: boolean;
}) {
    const { count, isActive, onClick, title } = useFilterChip(
        filterOption,
        'multiple',
        showDefaultSelected,
        isLoading,
        onFilterChipClick,
    );

    const chunks = chunk([...Array(optionCount).keys()], columns);
    const borderTopLeft = hasTopLeftBorderElement(chunks, index);
    const borderTopRight = hasTopRightBorderElement(chunks, columns, index);
    const borderBottomLeft = hasBottomLeftBorderElement(chunks, columns, index);
    const borderBottomRight = hasBottomRightBorderElement(chunks, columns, index);

    return (
        <div
            className={bem(styles.filterButton, {
                borderBottomLeft,
                borderBottomRight,
                borderTopLeft,
                borderTopRight,
                isActive: isActive,
            })}
            onClick={() => onClick()}
        >
            {filterOption.iconUrl && (
                <IconImage
                    className={styles.icon}
                    height={23}
                    url={filterOption.iconUrl}
                    width={23}
                />
            )}
            <div
                className={'font-size-12'}
                style={{ marginTop: filterOption.isDefault ? '20px' : undefined }}
            >
                {title}
            </div>
            {!filterOption.isDefault && (
                <div className={`font-size-10 ${count === null ? 'opacity-0' : ''}`}>({count})</div>
            )}
        </div>
    );
}

const hasTopLeftBorderElement = (chunks: number[][], index: number): boolean => {
    return chunks[0]?.[0] === index;
};

const hasTopRightBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const firstChunk = chunks[0] ?? [];

    switch (columns) {
        case 2:
            if (firstChunk.length > 1) {
                return firstChunk[1] === index;
            }
            return firstChunk[0] === index;
        case 3:
            if (firstChunk.length > 1) {
                return getLastElement(firstChunk) === index;
            }
            return firstChunk[0] === index;
        default:
            return firstChunk[0] === index;
    }
};

const hasBottomLeftBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const lastChunk = getLastElement(chunks) ?? [];
    const lastChunkElement = getLastElement(lastChunk);

    switch (columns) {
        case 2:
            if (lastChunk.length > 1) {
                return lastChunk[0] === index;
            }
            return lastChunkElement === index;
        case 3:
            return lastChunk[0] === index;
        default:
            return lastChunkElement === index;
    }
};

const hasBottomRightBorderElement = (chunks: number[][], columns: number, index: number): boolean => {
    const lastChunk = getLastElement(chunks) ?? [];
    const lastChunkElement = getLastElement(lastChunk);
    switch (columns) {
        case 2:
            if (lastChunk.length > 1) {
                return lastChunkElement === index;
            }
            return false;
        case 3:
            if (lastChunk.length === 3) {
                return lastChunkElement === index;
            }
            return false;
        default:
            return lastChunkElement === index;
    }
};
